import { useState } from 'react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Button, Modal, ModalHeader, ModalFooter, ModalCloseButton, TextField, DateTimePicker, NotificationDispatch, FormCheckbox } from '@tryhackme/thm-ui-components';
import { subDays } from 'date-fns';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useAddCompanyMutation } from '../../corporate.slice';
import { AddCompanyResponseData, CompanySelectOption, CreateCompanyModalProps, CreateCompanyProps } from '../../corporate.types';
import { StyledModalTitle, StyledTextField, StyledTrial, StyledAddIcon, StyledLabel } from './create-company-modal.styles';
import { createCompanyFormValidation } from './create-company-modal.constants';
export const CreateCompanyModal = ({
  onChange
}: CreateCompanyModalProps) => {
  const [addCompany] = useAddCompanyMutation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isCompanyTrial, setIsCompanyTrial] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>();
  const handleModalClose = () => {
    setModalOpen(false);
  };
  const {
    register,
    getValues,
    reset,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm<CreateCompanyProps>({
    defaultValues: {
      name: '',
      url: '',
      deadline: '',
      isTrial: false,
      emails: ''
    },
    mode: 'onChange',
    resolver: yupResolver(createCompanyFormValidation)
  });
  const onSubmit = async () => {
    try {
      const {
        name,
        url,
        emails
      }: CreateCompanyProps = getValues();
      const adminEmailsArray: string[] = emails.length > 0 ? emails.split(',').map((email: string) => email.trim()) : [];
      const result = await addCompany({
        name,
        url,
        deadline: date?.toString(),
        isTrial: isCompanyTrial,
        adminEmails: adminEmailsArray
      }).unwrap();
      const {
        id: dataId,
        name: dateName
      } = (result.data as AddCompanyResponseData);
      const newCompany: CompanySelectOption = {
        id: dataId,
        value: dateName,
        label: dateName
      };
      onChange(newCompany);
      NotificationDispatch('success', 'Company created successfully');
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'An unknown error has occurred');
    }
    reset();
    handleModalClose();
  };
  return <Modal onOpenChange={open => {
    setModalOpen(open);
    reset();
  }} open={modalOpen} triggerElement={<Button color="add" variant="solid">
          <StyledAddIcon icon={faPlus} /> Create company
        </Button>} isSmall data-sentry-element="Modal" data-sentry-component="CreateCompanyModal" data-sentry-source-file="create-company-modal.tsx">
      <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="create-company-modal.tsx">
        <StyledModalTitle isSmall data-sentry-element="StyledModalTitle" data-sentry-source-file="create-company-modal.tsx">Create company</StyledModalTitle>
      </ModalHeader>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id="add-users" method="dialog" onSubmit={handleSubmit(onSubmit)}>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="create-company-modal.tsx">
          <TextField {...register('name')} id="companyname-id" label="Company Name" placeholder="Company's name" variant="input" isError={!!errors.name?.message} errorMessage={errors.name?.message} autoComplete="off" data-sentry-element="TextField" data-sentry-source-file="create-company-modal.tsx" />
        </StyledTextField>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="create-company-modal.tsx">
          <TextField {...register('url')} id="companywebsite-id" label="Company Website" placeholder="Enter company URL" variant="input" isError={!!errors.url?.message} errorMessage={errors.url?.message} autoComplete="off" data-sentry-element="TextField" data-sentry-source-file="create-company-modal.tsx" />
        </StyledTextField>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="create-company-modal.tsx">
          <DateTimePicker {...register('deadline')} label="Deadline Date *" selected={date} placeholderText="None selected" minDate={subDays(new Date(), 30)} timeIntervals={15} isClearable shouldCloseOnSelect onChange={newDate => setDate(newDate)} autoComplete="off" required data-sentry-element="DateTimePicker" data-sentry-source-file="create-company-modal.tsx" />
        </StyledTextField>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="create-company-modal.tsx">
          <StyledLabel data-sentry-element="StyledLabel" data-sentry-source-file="create-company-modal.tsx">
            <FormCheckbox {...register('isTrial')} checked={isCompanyTrial} onCheckedChange={() => setIsCompanyTrial(!isCompanyTrial)} data-sentry-element="FormCheckbox" data-sentry-source-file="create-company-modal.tsx" />
            <StyledTrial data-sentry-element="StyledTrial" data-sentry-source-file="create-company-modal.tsx">This is a trial</StyledTrial>
          </StyledLabel>
        </StyledTextField>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="create-company-modal.tsx">
          <TextField {...register('emails')} id="email-id" label="Instructors' emails" placeholder="email 1, email 2, email 3," rows={4} variant="textarea" autoComplete="off" data-sentry-element="TextField" data-sentry-source-file="create-company-modal.tsx" />
        </StyledTextField>
        <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="create-company-modal.tsx">
          <ModalCloseButton text="Cancel" handleCloseButtonAction={() => reset()} data-sentry-element="ModalCloseButton" data-sentry-source-file="create-company-modal.tsx" />
          <Button type="submit" color="primary" variant="solid" data-sentry-element="Button" data-sentry-source-file="create-company-modal.tsx">
            Create company
          </Button>
        </ModalFooter>
      </form>
    </Modal>;
};